import Head from 'next/head'
import { FreezeDriedRawNuggets } from 'features/ECommerce/screens/ProductDetail/FreezeDriedRawNuggets'
import { prefetchProductVariantDetail } from 'features/ECommerce/api/queries/useFetchProductVariantDetail'
import { GetServerSidePropsContext } from 'next'
import { ProductVariantDetail } from 'models/Product'
import { useTranslation } from 'hooks/useTranslation'
import { prefetchShoppingCart } from 'features/ECommerce/api/queries/useFetchShoppingCart'
import Cookies from 'cookies'
import { CookieKeys } from 'services/Cookies'
import { sessionCookieName } from 'pages/api/auth/_cookieHelpers'
import { getPayloadFromJWT } from 'utils/jwt'
import Script from 'next/script'

const slug = 'freeze-dried-raw-nuggets'
const productId = 'fd-small-box-chicken'

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
  const cookies = new Cookies(ctx.req, ctx.res)

  const { dehydratedState, data } = await prefetchProductVariantDetail(productId)

  const authToken = cookies.get(sessionCookieName()) ?? cookies.get(CookieKeys.SESSION)
  const guestId = cookies.get(CookieKeys.GUEST_ID)
  const clientId = authToken ? getPayloadFromJWT(authToken).sub : guestId
  const dehydratedCartState = await prefetchShoppingCart(clientId || '')

  return {
    props: {
      ...dehydratedState,
      metaData: data || '',
      ...dehydratedCartState,
    },
  }
}

export default function FreezeDriedRawNuggetsPage({
  metaData,
}: {
  metaData: ProductVariantDetail
}) {
  const { t } = useTranslation()

  const pageTitle = t(`products:meta.${slug}.page-title`)
  const title = t(`products:meta.${slug}.title`)
  const description = t(`products:meta.${slug}.description`)

  const canonicalUrl = `https://wefeedraw.com/products/${slug}`

  // prettier-ignore
  const schema = metaData
    ? {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: `${title}`,
        url: `https://wefeedraw.com/products/${slug}`,
        image: `https://wefeedraw.com${metaData.image}`,
        description: description,
        brand: {
          '@type': 'Brand',
          name: 'We Feed Raw',
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'US',
          returnPolicyCategory: 'https://schema.org/MerchantReturnFiniteReturnWindow',
          merchantReturnDays: 30,
          returnMethod: 'https://schema.org/ReturnByMail',
          refundType: 'https://schema.org/FullRefund',
        },
        offers: {
          '@type': 'Offer',
          url: `https://wefeedraw.com/products/${slug}`,
          priceCurrency: 'USD',
          price: `${metaData.price.total / 100}`,
          availability: 'https://schema.org/InStock',
          seller: {
            '@type': 'Organization',
            name: 'We Feed Raw',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: '0.00',
              currency: 'USD',
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'US',
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 2,
                unitCode: 'DAY',
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 2,
                maxValue: 4,
                unitCode: 'DAY',
              },
            },
          },
          additionalProperty: {
            '@type': 'PropertyValue',
            name: 'Shipping',
            value: 'Free Shipping',
          },
        },
      }
    : {}

  const jsonLdString = JSON.stringify(schema)

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="title" content={title} />
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
      </Head>
      {metaData && (
        <Script
          id="schema-script"
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: jsonLdString }}
        />
      )}
      <FreezeDriedRawNuggets />
    </>
  )
}
